import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useContext } from 'react';

import ApiContext from '@/context/ApiContext/ApiContext';
import HealthStatusContext from '@/context/HealthStatusContext/HealthStatusContext';
import convertQueryKeyToUrl from '@/helpers/convertQueryKeyToUrl';
import { convertToQueryParams } from '@/helpers/convertToQueryParams';
import useLocale from '@/hooks/useLocale';

export enum PlannedMaintenanceType {
  WARNING = 'WARNING',
  INFO = 'INFO',
}

export interface PlannedMaintenance {
  id: string;
  type: PlannedMaintenanceType;
  message: string;
  title?: string;
}

export default function useGetPlannedMaintenance() {
  const { client } = useContext(ApiContext);
  const { areSystemsAvailable } = useContext(HealthStatusContext);
  const { pathname } = useRouter();
  const disabledUrls = [
    '/welcome',
    '/account-deleted',
    '/institution-not-found',
    '/institution-select',
    '/503',
  ];
  const {appLanguage} = useLocale();
  const queryParams = convertToQueryParams({
    language: appLanguage,
  });

  const { data, isPending } = useQuery<PlannedMaintenance, AxiosError>({
    queryKey: ['api', 'mhp-web', 'planned-maintenance', queryParams],
    queryFn: async ({ queryKey }) => {
      return (
        await client.current!.get<PlannedMaintenance>(convertQueryKeyToUrl(queryKey))
      ).data;
    },
    enabled: areSystemsAvailable && !disabledUrls.includes(pathname),
  });

  return {
    data, isPending,
  };
}
